import type { LexicalEditor, NodeKey } from "lexical";
import { useEffect, useRef, useState, type PointerEvent, type RefObject } from "react";
import {
  autoScrollSpeed,
  resolveDrop,
  type DropTarget,
  type UnitBox,
} from "../features/block-drag/geometry";
import { $planDrag, type DragPlan } from "../features/block-drag/plan";
import type { DispatchResult, EditorIntent } from "./handle";

/** What the layer draws while a drag is in progress. Layer-relative, except the preview. */
export interface DragFeedback {
  line: { top: number; left: number; width: number } | null;
  /** Viewport coordinates: the preview is `position: fixed` and follows the pointer. */
  preview: { x: number; y: number };
  label: string;
  count: number;
  /** `false` when letting go here would do nothing: outside the editor, or outside the list. */
  valid: boolean;
}

interface Session {
  plan: DragPlan;
  /** Measured once per unit until something that moves boxes happens. */
  boxes: Map<number, UnitBox>;
  x: number;
  y: number;
  target: DropTarget | null;
  valid: boolean;
  frame: number;
  release: () => void;
}

/** Movement before a press on the handle becomes a drag rather than a click. */
const THRESHOLD = 4;
/** How far outside the editor, sideways, the pointer may stray and still mean a drop. */
const SLACK = 96;

function scrollContainerOf(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node !== null; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY;
    const scrolls = overflow === "auto" || overflow === "scroll" || overflow === "overlay";
    if (scrolls && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
}

/**
 * What the block controls need from a drag implementation. The pointer-event one below is the
 * only one; an HTML5 drag-and-drop or a touch long-press source would satisfy the same interface.
 */
export interface DragAdapter {
  feedback: DragFeedback | null;
  /** Spread onto the handle. A press that never moves past the threshold is left to `onClick`. */
  handleProps(handleKey: NodeKey): {
    onPointerDown(event: PointerEvent<HTMLElement>): void;
    onPointerMove(event: PointerEvent<HTMLElement>): void;
    onPointerUp(event: PointerEvent<HTMLElement>): void;
    onPointerCancel(): void;
    onLostPointerCapture(): void;
  };
  /** Whether the press that just ended was a drag, so the click that follows can be ignored. */
  consumeClick(): boolean;
}

/**
 * Drag from the block handle. While the pointer moves, nothing in the document changes: the
 * insertion line and the preview are drawn in the layer. Letting go commits one `move-blocks`
 * intent, which looks the blocks up again by ID, so a target that has gone away in the meantime
 * is refused rather than guessed at. Escape, a cancelled pointer, or an invalid drop change
 * nothing at all, history included.
 */
export function useBlockDrag(
  editor: LexicalEditor,
  layerRef: RefObject<HTMLDivElement | null>,
  dispatch: (intent: EditorIntent) => DispatchResult,
): DragAdapter {
  const [feedback, setFeedback] = useState<DragFeedback | null>(null);
  const session = useRef<Session | null>(null);
  const press = useRef<{ x: number; y: number; key: NodeKey } | null>(null);
  const dragged = useRef(false);

  const end = (): void => {
    const current = session.current;
    session.current = null;
    press.current = null;
    if (current === null) return;
    cancelAnimationFrame(current.frame);
    current.release();
    setFeedback(null);
  };
  // Unmounting mid-drag must not leave listeners or a frame loop behind.
  useEffect(() => end, []);

  const track = (current: Session): void => {
    const root = editor.getRootElement();
    const layer = layerRef.current;
    if (root === null || layer === null) return;
    const measure = (index: number): UnitBox | null => {
      const cached = current.boxes.get(index);
      if (cached !== undefined) return cached;
      const keys = current.plan.units[index]?.keys ?? [];
      const first = editor.getElementByKey(keys[0] ?? "");
      const last = editor.getElementByKey(keys[keys.length - 1] ?? "");
      if (first === null || last === null) return null;
      const box = {
        top: first.getBoundingClientRect().top,
        bottom: last.getBoundingClientRect().bottom,
      };
      current.boxes.set(index, box);
      return box;
    };
    const parent = editor.getElementByKey(current.plan.parentKey) ?? root;
    const bounds = parent.getBoundingClientRect();
    const editorBox = root.getBoundingClientRect();
    const sideways = current.x >= editorBox.left - SLACK && current.x <= editorBox.right + SLACK;
    // A list item may only land in its own list: leaving the list is not a drop.
    const within =
      !current.plan.inList || (current.y >= bounds.top - 8 && current.y <= bounds.bottom + 8);
    const target = sideways && within ? resolveDrop(current.plan.units, measure, current.y) : null;
    current.target = target;
    current.valid = target !== null;
    const base = layer.getBoundingClientRect();
    setFeedback({
      line:
        target === null || target.unchanged
          ? null
          : { top: target.lineY - base.top, left: bounds.left - base.left, width: bounds.width },
      preview: { x: current.x, y: current.y },
      label: current.plan.label,
      count: current.plan.count,
      valid: current.valid,
    });
  };

  const start = (key: NodeKey, x: number, y: number): void => {
    const plan = editor.read(() => $planDrag(key));
    const root = editor.getRootElement();
    if (plan === null || root === null) return;
    const scroller = scrollContainerOf(root);
    const invalidate = (): void => {
      session.current?.boxes.clear();
      if (session.current !== null) track(session.current);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      end();
    };
    // Present only for the length of a drag, and only for Escape.
    window.addEventListener("keydown", onKey, { capture: true });
    window.addEventListener("scroll", invalidate, { capture: true, passive: true });
    window.addEventListener("resize", invalidate);
    const offUpdate = editor.registerUpdateListener(invalidate);

    const step = (): void => {
      const current = session.current;
      if (current === null) return;
      const visible = scroller?.getBoundingClientRect() ?? { top: 0, bottom: window.innerHeight };
      const speed = autoScrollSpeed(current.y, {
        top: Math.max(visible.top, 0),
        bottom: Math.min(visible.bottom, window.innerHeight),
      });
      // Scroll whatever actually scrolls: a panel the editor sits in, or else the page.
      if (speed !== 0) (scroller ?? window).scrollBy(0, speed);
      current.frame = requestAnimationFrame(step);
    };

    session.current = {
      plan,
      boxes: new Map(),
      x,
      y,
      target: null,
      valid: false,
      frame: requestAnimationFrame(step),
      release: () => {
        window.removeEventListener("keydown", onKey, { capture: true });
        window.removeEventListener("scroll", invalidate, { capture: true });
        window.removeEventListener("resize", invalidate);
        offUpdate();
      },
    };
    dragged.current = true;
    track(session.current);
  };

  return {
    feedback,
    handleProps: (handleKey) => ({
      onPointerDown(event) {
        if (event.button !== 0 || !editor.isEditable()) return;
        dragged.current = false;
        press.current = { x: event.clientX, y: event.clientY, key: handleKey };
        event.currentTarget.setPointerCapture(event.pointerId);
      },
      onPointerMove(event) {
        const current = session.current;
        if (current !== null) {
          current.x = event.clientX;
          current.y = event.clientY;
          return track(current);
        }
        const down = press.current;
        if (down === null) return;
        const moved = Math.hypot(event.clientX - down.x, event.clientY - down.y);
        if (moved >= THRESHOLD) start(down.key, event.clientX, event.clientY);
      },
      onPointerUp() {
        const current = session.current;
        if (current !== null) {
          // Measured again, because the boxes are only as new as the last scroll event, and a
          // scroll says so a frame after it happens: a drop right after the panel scrolled would
          // go where the blocks were. It goes where they are.
          current.boxes.clear();
          track(current);
        }
        const target = current?.target ?? null;
        const drop =
          current !== null && current.valid && target !== null && !target.unchanged
            ? ({
                type: "move-blocks",
                blockIds: current.plan.sourceIds,
                beforeId: target.beforeId,
              } as const)
            : null;
        end();
        if (drop !== null) dispatch(drop);
      },
      onPointerCancel: end,
      onLostPointerCapture: end,
    }),
    consumeClick() {
      const was = dragged.current;
      dragged.current = false;
      return was;
    },
  };
}
